// src/api/userManagementService.ts
import {AxiosResponse} from 'axios';
import {IFilterData} from '../components/bulk-user-upload-module/models';
import {IFiltersData} from '../components/pages/WelcomeScreenSingleClient';
import {ManageTeamMemberRequestPayload, UserDetail} from '../types/types';
import {
  BulkUploadEditUserRequest,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserStatusRequest,
  UserFeedbackPayload,
} from '../types/user-management/userManagementTypes';
import {isValidGuid} from '../utils/utils';
import apiClient from './index';
interface IGetUsersCount {
  searchTerm?: string | null;
  clientIds?: string;
  permissionGroupIds?: string;
  projectIds?: string;
}
const version = 'v1';
const userManagementEndpoint = (path: string) =>
  `${version}/usermanagement${path}`;

const clientUserManagementEndpoint = (path: string) =>
  `/${version}/clientmanagement${path}`;

export const getUsers = (params?: string) => {
  return apiClient.get(clientUserManagementEndpoint('/users' + params));
};

export const getTeamMembers = (params: ManageTeamMemberRequestPayload) => {
  const endpoint = clientUserManagementEndpoint('/manage-team-members');
  return apiClient.get(endpoint, {params});
};

export const getUsersCount = (params?: IGetUsersCount) => {
  return apiClient.get(clientUserManagementEndpoint('/users/count'), {
    params: params,
  });
};

export const getUserByEmail = (email: string) => {
  return apiClient.get(userManagementEndpoint(`/users/detail/${email}`));
};

export const getUserFindByEmail = (email: string) => {
  return apiClient.get(userManagementEndpoint(`/users/finds/${email}`));
};

export const createUser = (userData: CreateUserRequest) => {
  return apiClient.post(userManagementEndpoint('/users'), userData);
};

export const updateTeamMember = (teamMemberInfo: UpdateUserRequest) => {
  return apiClient.put(userManagementEndpoint(`/users`), teamMemberInfo);
};

export const updateUserStatusAPI = (
  userStatusData: UpdateUserStatusRequest
) => {
  return apiClient.put(userManagementEndpoint(`/users/status`), userStatusData);
};

export const getClientsList = (params?: string) => {
  return apiClient.get(
    clientUserManagementEndpoint(`/clients/me?` + (params ?? ''))
  );
};

// Bulk upload
export const bulkUploadUsers = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.post(userManagementEndpoint('/bulkupload'), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const bulkUploadUsersFileProcessing = (file: File, clientId: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('clientId', clientId);

  return apiClient.post(userManagementEndpoint('/bulkupload'), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const bulkUploadUsersTable = (
  BatchJobImportId: string,
  filterData: IFilterData
) => {
  return apiClient.get(
    userManagementEndpoint(`/bulkupload/users/${BatchJobImportId}`),
    {
      params: filterData,
    }
  );
};

// Here we expecting blob response
export const bulkUploadDownloadTemplate = async (
  clientId: string
): Promise<AxiosResponse<Blob>> => {
  return await apiClient.get<Blob>(
    userManagementEndpoint(`/download/template/${clientId}`),
    {
      responseType: 'blob',
    }
  );
};

export const bulkDownloadErrorTemplate = async (
  batchJobImportId: string
): Promise<AxiosResponse<Blob>> => {
  return await apiClient.get<Blob>(
    userManagementEndpoint(`/download/erroruser/${batchJobImportId}`),
    {
      responseType: 'blob',
    }
  );
};

export const bulkUploadEditUser = (payloadData: BulkUploadEditUserRequest) => {
  return apiClient.put(userManagementEndpoint('/bulkupload/user'), payloadData);
};

export const saveBulkUploadUsers = (batchJobImportId: string) => {
  return apiClient.post(userManagementEndpoint(`/saveBulkUploadUsers`), {
    batchJobImportId: batchJobImportId,
  });
};

export const resendInvite = (userIdList: string[]) => {
  console.log('userIdList:', userIdList);

  if (!Array.isArray(userIdList)) {
    throw new TypeError('userIdList must be an array');
  }

  const payload = {
    userIdList,
  };

  return apiClient.post(userManagementEndpoint(`/users/resend`), payload);
};

export const getHostingSummaryAlerts = (
  clientId: string,
  projectId: string,
  workspaceId: string
) => {
  const endpoint = userManagementEndpoint('/alerts/hosting-user-summary');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params});
};

export const getProjectsMe = (
  clientId: string,
  body: (IFiltersData | Record<string, unknown>) | string,
  params?: string
) => {
  return apiClient.post(
    clientUserManagementEndpoint(
      `/clients/${clientId}/projects/me?` + (params ?? '')
    ),
    body
  );
};

export const getProjectOverviewFilterOptions = (clientId: string) => {
  return apiClient.get(
    clientUserManagementEndpoint(
      `/clients/${clientId}/projectOverviewFilterOption/me`
    )
  );
};

export const getProjectOverviewCount = (
  clientId: string,
  body: (IFiltersData | Record<string, unknown>) | string,
  params?: string
) => {
  return apiClient.post(
    clientUserManagementEndpoint(
      `/clients/${clientId}/projectOverviewCount/me?` + (params ?? '')
    ),
    body
  );
};

export const getOverviewProjects = (
  clientId: string,
  body: (IFiltersData | Record<string, unknown>) | string,
  params?: string
) => {
  return apiClient.post(
    clientUserManagementEndpoint(
      `/clients/${clientId}/clientProjects/me?` + (params ?? '')
    ),
    body
  );
};

export const pinUnpinProject = (
  projectId: string,
  clientId: string,
  value: string
) => {
  return apiClient.post(
    userManagementEndpoint(
      `/clients/${clientId}/projects/${projectId}/${value}`
    )
  );
};

export const pinProjectInAllActiveGrid = (projectId: string, clientId: string) => {
  return apiClient.post(userManagementEndpoint(`/projects/${projectId}/pin`), {
    clientId,
  });
};

export const unpinProjectInAllActiveGrid = (
  projectId: string,
  clientId: string
) => {
  return apiClient.post(userManagementEndpoint(`/projects/${projectId}/unpin`), {
    clientId,
  });
};

export const getInvoiceAlerts = (clientId: string, projectId: string) => {
  const endpoint = userManagementEndpoint('/alerts/invoice');
  const params = {
    clientId,
    projectId,
  };

  return apiClient.get(endpoint, {params});
};

export const getProcessingSummaryAlerts = (
  clientId: string,
  projectId: string,
  workspaceId: string,
  signal?: AbortSignal
) => {
  const endpoint = userManagementEndpoint('/alerts/processing-summary');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params, signal: signal});
};

export const getHighLevelAlerts = (
  clientId: string,
  projectId: string,
  workspaceId: string
) => {
  const endpoint = userManagementEndpoint('/alerts/high-level-productions');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params});
};

export const getProcessingExceptionsAlerts = (
  clientId: string,
  projectId: string,
  workspaceId: string,
  signal?: AbortSignal
) => {
  const endpoint = userManagementEndpoint('/alerts/processing-exceptions');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };

  return apiClient.get(endpoint, {params, signal: signal});
};

export const updateAlertStatusAsViewed = (userAlertId: number) => {
  return apiClient.put(
    userManagementEndpoint(`/alerts/${userAlertId}/status/viewed`)
  );
};

export const getWelcomeScreenAlert = (
  clientId: string,
  projectId: string,
  workspaceId: string | null
) => {
  const endpoint = userManagementEndpoint('/alerts/welcome-screen');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };
  return apiClient.get(endpoint, {params});
};

export const getDatesetProcessingCompletedAlert = (
  clientId: string,
  projectId: string,
  workspaceId?: string
) => {
  const endpoint = userManagementEndpoint('/project-alerts/processing-summary');
  const params = {
    clientId,
    projectId,
    workspaceId,
  };
  return apiClient.get(endpoint, {params});
};

export const getDatesetProcessingExceptionAlert = (
  clientId: string,
  projectId: string,
  workspaceId?: string
) => {
  const endpoint = userManagementEndpoint(
    '/project-alerts/processing-exceptions'
  );
  const params = {
    clientId,
    projectId,
    workspaceId,
  };
  return apiClient.get(endpoint, {params});
};

export const getUserDetailsByUserIds = (userIdList: string[]) => {
  if (!Array.isArray(userIdList)) {
    throw new TypeError('userIdList must be an array');
  }

  const payload = {
    userIds: userIdList,
  };

  return apiClient.post(userManagementEndpoint(`/users/details`), payload);
};

/** Parses POST /users/details response (array at `data` or `data.value`). */
export const parseUsersDetailListResponse = (response: {
  data?: {data?: UserDetail[] | {value?: UserDetail[]}};
}): UserDetail[] => {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === 'object' && Array.isArray(payload.value)) {
    return payload.value;
  }
  return [];
};

/** Resolves audit user GUIDs to names via POST /usermanagement/users/details. */
export const fetchUserDetailsByIds = async (
  userIdList: string[]
): Promise<UserDetail[]> => {
  const userIds = [...new Set(userIdList.filter(isValidGuid))];
  if (userIds.length === 0) {
    return [];
  }
  const response = await getUserDetailsByUserIds(userIds);
  return parseUsersDetailListResponse(response);
};

export const getCustomReportsAlert = (clientId: string) => {
  const endpoint = userManagementEndpoint('/alerts/client-level');
  const params = {
    clientId,
  };
  return apiClient.get(endpoint, {params});
};

export const validateInviteToken = (InvitedToken: string) => {
  return apiClient.post(userManagementEndpoint(`/users/token`), {
    InvitedToken: InvitedToken,
  });
};

export const feedbackUploadAttachment = (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file); // append each file under the key "files"
  });

  return apiClient.post(
    userManagementEndpoint('/feedback/upload-attachments'),
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

export const deleteFeedbackAttachment = (attachmentId: number) => {
  return apiClient.delete(
    userManagementEndpoint(`/feedback/attachments/${attachmentId}`)
  );
};

export const submitFeedback = (payload: UserFeedbackPayload) => {
  return apiClient.post(userManagementEndpoint('/feedback/submit'), payload);
};

export const getFeatureSettings = () => {
  return apiClient.get(userManagementEndpoint(`/featuresettings`));
};

export const getDeepLinkConfig = () => {
  return apiClient.get(userManagementEndpoint('/deeplink-configuration'));
};

export const getIsMaintenanceEnabled = async () : Promise<boolean> => {
  try {
      const response = await apiClient.get(userManagementEndpoint(`/featuresettings/maintenance`));
      return Boolean(response.data.maintenanceMode);
    } catch {
      return true;
    }
};